import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import {
  Router,
  NavigationStart,
  NavigationCancel,
  NavigationEnd,
} from '@angular/router'
import { apiConfig } from '@app/app-config';
import { Subject, Observable } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { TranslatorService } from './core/services/translator/translator.service'
import { filter } from 'rxjs/operators'

import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionType,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
  AuthenticationResult,
  AuthError,
} from '@azure/msal-browser';
import { HttpClient } from '@angular/common/http'

import { environment } from '@env/environment';
import { b2cPolicies } from './shared/common/app-config'
import { MainComponent } from './home/home.component'
import { SharedVariable } from './custom-shared/util/shared-variable'
import { MasterClienteCreateUserService } from './features/master/master-cliente/cliente-criar-usuario-stepper/master-cliente-create-user.service';
import { CookieService } from 'ngx-cookie-service';

const QLIK_URL = environment.qlikUrl;
const QLIK_PORT = environment.qlikPort;
const QLIK_PREFIX = environment.qlikPrefix;
const QLIK_IS_SECURE = environment.qlikIsSecure;
const BASE_PATH = environment.basePath

interface IdTokenClaims extends AuthenticationResult {
  idTokenClaims: {
    acr?: string;
  };
}

/**
 * The main entry point component
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * The show loading option
   */
  showLoading
  loaderClients
  /**
   * The Subjet emitter object to destroy all the subscriptions
   * when the component is destroyed
   */
  destroy$: Subject<boolean> = new Subject<boolean>()
  /**
   * The Subjet initializer emitter object
   */
  private initialized$: Observable<boolean>

  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  appVersion = environment.appVersion;
  constructor(
    private httpClient: HttpClient,
    private masterClienteCreateUserService: MasterClienteCreateUserService,
    private router: Router,
    private translator: TranslatorService,
    private cdRef: ChangeDetectorRef,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private mainComponent: MainComponent,
    private authService: MsalService,
    public shared: SharedVariable,
    private msalBroadcastService: MsalBroadcastService,
    private cookieService: CookieService
  ) {

    this.translator.initLanguage()
    this.showLoading = true
  }

  closeDate() {
    localStorage.setItem("dateDec", JSON.stringify(new Date().getTime()))
    setTimeout(() => {
      this.closeDate();
    }, 500);
  }
  pageReloaded: any;

  ngOnInit(): void {
    this.setLoginDisplay();
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
    this.pageReloaded = window.performance
      .getEntriesByType('navigation')
      .map((nav) => (nav as any).type)
      .includes('reload');
    if (!window.location.hash) {
      if (localStorage.getItem("dateDec") && (((new Date().getTime() - parseInt(localStorage.getItem("dateDec"))) / 1000) >= 5) && !this.pageReloaded) {
        setTimeout(() => {
          this.logout();
          return;
        }, 2000);
      }
    } else {
      localStorage.removeItem('dateDec');
    }
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        let payload: IdTokenClaims = <AuthenticationResult>result.payload;
        this.setLoginDisplay();
        if (result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          this.loadExternalData();
        }
        // We need to reject id tokens that were not issued with the default sign-in policy.
        // "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr")
        // To learn more about b2c tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
        return result;
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        if (result.error instanceof AuthError) {
          this.setLoginDisplay();
          
          // Learn more about AAD error codes at https://docs.microsoft.com/azure/active-directory/develop/reference-aadsts-error-codes
          if (result.error.message.includes('AADB2C90118')) {
            // login request with reset authority
            let resetPasswordFlowRequest = {
              scopes: ['openid'],
              authority: b2cPolicies.authorities.forgotPassword.authority,
            };
            this.login(resetPasswordFlowRequest);
          }
        }
      });

    this.closeDate();
  }

  loadExternalData() {
    setTimeout(() => {
      if (localStorage.getItem('externalDataLoaded')) {
        return;
      }
      localStorage.setItem('externalDataLoaded', 'true');
      // ❌ stockExchangeList - Chargé via loadGlobalData() dans les guards et load-files
      // ❌ currencyList - Chargé via loadGlobalData() dans les guards et load-files
      // ❌ instructionFundList - Chargé via loadGlobalData() dans les guards et load-files
      // ❌ clearingList - Chargé via loadGlobalData() dans les guards et load-files
      // ❌ brokerList - Chargé via loadGlobalData() dans les guards et load-files
      // ❌ cambioBrokerList - Chargé via loadPaymentOrderData() dans Payment Order guard et load-files
      // ❌ counterPartyList - Chargé via loadGlobalData() dans les guards et load-files
      //!this.shared.tradableAssetList ? this.shared.getTradableAsset() : false;
      //!this.shared.bloackingReasonsList ? this.shared.getBloackingReasonsList() : false;
      !this.shared.fundList.length ? this.shared.getFundList() : false;
      //!this.shared.placeOfSettlementList ? this.shared.getPlaceOfSettlementList() : false;
      //!this.shared.lastroList ? this.shared.getLastroList() : false;
    }, 1000)
  }

  /**
   * Subscribes to the events stream in order to display a loader when a request is sent
   */
  ngAfterViewInit() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.showLoading = true
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel
      ) {
        this.showLoading = false
      }
      this.cdRef.detectChanges()
    })
  }
  /**
   * Destroys the component and all the subscriptions
   */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      } else {
        this.authService
          .loginPopup(userFlowRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect(userFlowRequest);
      }
    }
  }

  async logout() {
    let loginDisplay = this.authService.instance.getAllAccounts()[0];
    await this.authService
      .acquireTokenSilent({
        account: loginDisplay,
        redirectUri: environment.msalUrl,
        scopes: apiConfig.scopes,
      }).toPromise()
      .then(async authResult => {
        await this.authService.logout({ idTokenHint: `${authResult.idToken}` })
          .toPromise()
          .then(() => {
            sessionStorage.clear();
            localStorage.clear();
            this.cookieService.deleteAll('/', 'b2clogin.com');
          });
      });
  }


}
